import { useMemo, type JSX } from 'react';
import debounce from 'debounce';

import { DurationType } from 'dg-web-shared/dto/PermitTimeUnit.ts';
import { Formatter } from 'dg-web-shared/lib/Date.ts';
import { useLanguage } from 'dg-web-shared/lib/Localization.tsx';
import { Store, useStore } from 'dg-web-shared/lib/Flux.tsx';
import { Response } from 'dg-web-shared/lib/HttpResponse.ts';
import { Message } from 'dg-web-shared/lib/Localized.ts';
import * as Http from '../../api/Http.ts';
import * as AsyncRequest from '../../AsyncRequest.ts';
import * as ClearancePermitListState from '../../clearance-permit-list/state/ClearancePermitListState.ts';
import * as ResultState from '../../clearance-permit-list/state/ResultState.ts';
import * as OperatorDataState from '../../common/state/OperatorDataState.ts';
import * as PermitTypeState from '../../common/state/PermitTypeState.ts';
import {
    getIdentificationTypesGivenSelection,
    hasBadgeIdentification,
    hasLicensePlateIdentification,
} from '../../common/state/PermitTypeState.ts';
import { ButtonSpecialState } from '../../ui/buttons/IconButton.tsx';
import { LabeledText } from '../../ui/labeled-elements/LabeledText.tsx';
import { SingleSelection } from '../../ui/labeled-elements/SingleSelection.tsx';
import {
    ConfirmSaveHeader,
    ErrorHeader,
    FullSlideInLeftColumn,
    FullSlideInRightColumn,
    LoaderHeader,
    SlideInBody,
} from '../../ui/slidein/Slidein.tsx';
import * as PermitCreateTexts from '../i18n/PermitCreateTexts.ts';
import * as AddressState from '../state/AddressState.ts';
import * as BadgeState from '../state/BadgeState.ts';
import * as LicensePlateState from '../state/LicensePlateState.ts';
import * as PermitCreateState from '../state/PermitCreateState.ts';
import * as PermitEditState from '../state/PermitEditState.ts';
import { AddressSelection } from './Address.tsx';
import { CounterPaymentChannelEdit } from './CounterPaymentChannel.tsx';
import { DurationField, EndDateField } from './PermitCreateSubComponents.tsx';
import {
    getDurationFromId,
    getDurationString,
} from './PermitTimeUnitHelpers.ts';
import {
    AdditionalInfos,
    Badges,
    Email,
    LicensePlates,
    Remark,
    renderPrice,
} from './Shared.tsx';
import {
    BadgesThatNeedAccountNotification,
    BadgesThatNeedWhitelistNotification,
    LpsThatNeedWhitelistNotification,
    OverlappingEntitiesNotification,
} from './SharedNotifications.tsx';
import {
    ContractQuotaInfos,
    ContractQuotaInfosPerEntity,
    ContractQuotaInfoType,
    keyForQuotaInfo,
} from 'dg-web-shared/common/components/quota-info/ContractQuotaInfos.ts';
import {
    Notification,
    NotificationColor,
    NotificationIcon,
} from '../../ui/notifications/Notification.tsx';
import { DateTime, Duration } from 'luxon';
import { Localized } from 'dg-web-shared/lib/Localization.tsx';
import {
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
} from '@mui/material';
import * as CurrentOperatorLoginState from '../../common/state/CurrentOperatorLoginState.ts';

const PERMITTYPES_WITH_REQUIRED_ADDRESS = [
    4604, 4605, 4606, 4607, 4608, 4609, 4610, 4611, 4612, 4613, 4614, 4615,
    4616, 4617, 4705, 4618, 4619, 4620, 4621, 4622, 4623, 4624, 4625, 4626,
    4627, 4628, 4629, 4630, 4631, 4706, 4149, 4632, 4633, 4729, 4725,
];

export const closePermitCreateSlidin = (store: Store) => {
    PermitCreateState.resetAllState(store);
    AddressState.resetAll(store);
};

const abortPermitCreateProcess = (store: Store): string => {
    closePermitCreateSlidin(store);
    return 'PermitCreateSlideIn-abortPermitCreateProcess';
};

const removeLicensePlate = (store: Store, id: number): string => {
    PermitCreateState.Config.set(
        store,
        (s: PermitCreateState.Config.State): PermitCreateState.Config.State => {
            s.licensePlates = s.licensePlates.filter(lp => lp.id !== id);
            return s;
        },
    );
    PermitCreateState.Calc.reset(store);
    AddressState.ServerList.reset(store);
    return 'PermitCreateSlideIn-removeLicensePlate';
};

const removeBadge = (store: Store, id: number): string => {
    PermitCreateState.Config.set(
        store,
        (s: PermitCreateState.Config.State): PermitCreateState.Config.State => {
            s.badges = s.badges.filter(badge => badge.id !== id);
            return s;
        },
    );
    PermitCreateState.Calc.reset(store);
    return 'PermitCreateSlideIn-removeLicensePlate';
};

const clearAddress = (store: Store): string => {
    AddressState.Edit.reset(store);
    AddressState.Select.reset(store);
    AddressState.ServerList.reset(store);
    PermitCreateState.Config.stateWrite(store, { address: null });
    return 'PermitCreateSlideIn-clearAddress';
};

const createPermit = AsyncRequest.request(
    Http.OperatorAccount.Permits.create,
    (store: Store, res: Response): string => {
        PermitCreateState.CreateResponse.setResponse(store, res);

        if (res.statusCode.cls.success) {
            PermitEditState.Server.setResponse(store, res);
            const serverData = PermitEditState.Server.get(store).data;

            if (serverData) {
                new ClearancePermitListState.StateSlice(store).selectPermit(
                    serverData.contractExternalId,
                );
            }

            PermitCreateState.resetAllState(store);
            AddressState.resetAll(store);
            ResultState.refetchSameContext(store, true);
        }
        return 'PermitCreateSlideIn-createPermit';
    },
);

const editAddress = (store: Store) => {
    AddressState.Select.stateWrite(store, { selectAddressOpen: true });
    if (AddressState.getLicensePlates(store).length === 0) {
        AddressState.Select.stateWrite(store, { addressSelected: true });
    }
    return 'PermitCreateSlideIn-editAddress';
};

const getIdentificationTypes = (s: {
    permitTypes: PermitTypeState.State;
    config: PermitCreateState.Config.State;
}) =>
    getIdentificationTypesGivenSelection(
        s.permitTypes.data,
        s.config.permitTypeId ? [s.config.permitTypeId] : [],
    );

export const zoneToString = (z: PermitTypeState.Zone): string =>
    `${z.extZoneCode ? z.extZoneCode + ' — ' : ''}${z.name} — ${z.city}`;

function zoneSelectionString(
    zones: PermitTypeState.Zone[],
): JSX.Element | null {
    if (zones.length === 0) {
        return null;
    } else {
        return (
            <div>
                {zones.map((z: PermitTypeState.Zone) => (
                    <div key={z.id}>{zoneToString(z)}</div>
                ))}
            </div>
        );
    }
}

export function PermitCreate() {
    const { storeState, update } = useStore(store => ({
        config: PermitCreateState.Config.get(store),
        calc: PermitCreateState.Calc.get(store),
        layout: PermitCreateState.Layout.get(store),
        addLp: LicensePlateState.AddTripple.get(store),
        permitType: PermitTypeState.getById(
            store,
            PermitCreateState.Config.get(store).permitTypeId,
        ),
        permitTypes: PermitTypeState.get(store),
        createResponse: PermitCreateState.CreateResponse.get(store),
        selectedLicensePlate: LicensePlateState.AddServerData.get(store),
        licensePlate: LicensePlateState.Entity.get(store),
        operatorData: OperatorDataState.get(store),
        currentLogin: CurrentOperatorLoginState.get(store),
    }));
    const { language } = useLanguage();

    const resetCalc = useMemo(
        () =>
            debounce(() => {
                update(PermitCreateState.Calc.reset);
            }, 500),
        [update],
    );

    function txt() {
        return PermitCreateTexts.texts[language];
    }

    function renderZones(permitType: PermitTypeState.PermitType): JSX.Element {
        if (permitType.selectZones === 'NO_CHOICE') {
            return (
                <LabeledText label={txt().ValidZones()}>
                    {permitType.zones.length > 10 ? (
                        txt().FixedZones(permitType.zones.length)
                    ) : (
                        <div>
                            {permitType.zones.map(z => (
                                <div key={z.id}>{zoneToString(z)}</div>
                            ))}
                        </div>
                    )}
                </LabeledText>
            );
        } else {
            const selectedZones = permitType.zones.filter(z =>
                storeState.config.onstreetZones
                    ? storeState.config.onstreetZones.indexOf(z.id) > -1
                    : false,
            );

            return (
                <SingleSelection
                    label={txt().ValidZones()}
                    onClick={() =>
                        update(store =>
                            PermitCreateState.Layout.stateWrite(store, {
                                zonePickerOpen: true,
                            }),
                        )
                    }
                    selection={zoneSelectionString(selectedZones)}
                    errorText={
                        storeState.layout.confirmPressedOnError &&
                        selectedZones.length === 0
                            ? txt().MissingZone()
                            : null
                    }
                    focused={storeState.layout.zonePickerOpen}
                />
            );
        }
    }

    function getSelectedDurationString(): string {
        const permitType = storeState.permitType;
        if (permitType && permitType.validDurationsV2) {
            const duration = getDurationFromId(
                storeState.config.durationId,
                permitType.validDurationsV2,
            );
            if (duration) {
                return getDurationString(duration, language);
            }
        }
        return '';
    }

    function validityIntervalErrorText() {
        if (storeState.layout.confirmPressedOnError && noValidityInterval()) {
            return txt().ValidityIntervalMissing();
        }

        return null;
    }

    function permitTypeErrorText() {
        if (storeState.layout.confirmPressedOnError && !storeState.permitType) {
            return txt().PermitTypeMissing();
        }

        return null;
    }

    function selectableDurationsCount(): number {
        return storeState.permitType?.validDurationsV2?.length ?? 0;
    }

    function isDurationSelectable(): boolean {
        return selectableDurationsCount() > 0;
    }

    function noValidityInterval(): boolean {
        const { durationId, startDate, endDate } = storeState.config;
        const canSelectDuration = isDurationSelectable();
        return (
            !startDate ||
            (canSelectDuration && !durationId) ||
            (!canSelectDuration && !endDate)
        );
    }

    function zonesSelected(): boolean {
        const permitType = storeState.permitType;
        const selectedZones = storeState.config.onstreetZones;
        if (permitType) {
            switch (permitType.selectZones) {
                case 'NO_CHOICE':
                    return true;
                case 'SELECT_ONE':
                    return !!selectedZones && selectedZones.length === 1;
                case 'SELECT_MANY':
                    return !!selectedZones && selectedZones.length >= 1;
                default:
                    return false;
            }
        } else {
            return false;
        }
    }

    function incompleteData(): boolean {
        if (
            PERMITTYPES_WITH_REQUIRED_ADDRESS.indexOf(
                storeState.permitType?.id ?? -1,
            ) > -1 &&
            !storeState.config.address
        ) {
            return true;
        }

        if (noValidityInterval()) {
            return true;
        }

        if (
            hasLicensePlateIdentification(getIdentificationTypes(storeState)) &&
            storeState.config.licensePlates.length === 0
        ) {
            return true;
        }

        if (
            hasBadgeIdentification(getIdentificationTypes(storeState)) &&
            storeState.config.badges.length === 0
        ) {
            return true;
        }

        if (!zonesSelected()) {
            return true;
        }

        if (
            storeState.permitType &&
            storeState.permitType.mubstBeLinkedToCustomerAccount &&
            !storeState.config.email
        ) {
            return true;
        }

        const calc = storeState.calc.data;
        if (calc) {
            if (calc.emailIsNotALogin) {
                return true;
            }

            if (
                (calc.overlappingLpPermits &&
                    calc.overlappingLpPermits.length > 0) ||
                (calc.overlappingBadgePermits &&
                    calc.overlappingBadgePermits.length > 0)
            ) {
                return true;
            }

            if (calc.lpsThatNeedWhitelist.length > 0) {
                return true;
            }
        }

        if (
            storeState.operatorData.data?.requireCounterPaymentChannel &&
            !storeState.config.counterPaymentChannel
        ) {
            return true;
        }

        return false;
    }

    function confirmButtonSpecialState(): ButtonSpecialState | null {
        if (
            storeState.calc.pending ||
            storeState.createResponse.pending ||
            !checkQuotaApprovalRights()
        ) {
            return ButtonSpecialState.DISABLED;
        }
        if (incompleteData() && storeState.layout.confirmPressedOnError) {
            return ButtonSpecialState.ERROR;
        }
        if (incompleteData()) {
            return ButtonSpecialState.DISABLED;
        } else {
            return null;
        }
    }

    function isEndDateSelectable(): boolean {
        const notSelectable =
            storeState.permitType &&
            storeState.config.endDate &&
            storeState.permitType.toRange &&
            storeState.permitType.toRange.start.toMillis() ===
                storeState.permitType.toRange.end.toMillis();
        return !notSelectable;
    }

    function getPriceString(): string {
        const calc = storeState.calc.data;
        if (storeState.calc.pending) {
            return '...';
        }
        if (!calc) {
            return txt().PriceNotAvailable();
        } else {
            return renderPrice(calc, language);
        }
    }

    function getValidToCalcString(): string | null {
        const calc = storeState.calc.data;
        const startDate = storeState.config.startDate;
        const permitType = storeState.permitType;
        if (permitType && permitType.timeSelectionType === 'from_to') {
            return null;
        }
        if (storeState.calc.pending) {
            return '...';
        }
        if (!calc) {
            const durationId = storeState.config.durationId;

            if (
                !!startDate &&
                !!permitType &&
                !!durationId &&
                !storeState.config.endDate
            ) {
                const duration = getDurationFromId(
                    durationId,
                    permitType.validDurationsV2 || [],
                );

                if (duration) {
                    /*
                       this is super hacky but date calculation is done in fronted and desperately
                       needs refactor. No time to do that.

                       Therefore simple split to retain old logic.
                     */

                    const startDateLuxon = startDate;

                    const now = DateTime.now();

                    if (duration.type === 'hour') {
                        const exactStart =
                            startDateLuxon < now && duration.type === 'hour'
                                ? now
                                : startDateLuxon;
                        return Formatter.dayMonthYearHourMinute(
                            exactStart
                                .plus(
                                    toLuxonDuration(
                                        duration.type,
                                        duration.quantity,
                                    ),
                                )
                                .minus({ seconds: 1 }),
                        );
                    } else {
                        return Formatter.dayMonthYear(
                            startDateLuxon
                                .plus(
                                    toLuxonDuration(
                                        duration.type,
                                        duration.quantity,
                                    ),
                                )
                                .minus({ days: 1 }),
                        );
                    }
                }
            }
            return txt().DateNotAvailable();
        } else {
            return Formatter.dayMonthYearHourMinute(calc.validTo);
        }
    }

    function renderNotification(): JSX.Element | null {
        const calc = storeState.calc.data;
        const quotasWrite =
            storeState.currentLogin.data?.permissions.quotasWrite || false;
        if (calc) {
            if (calc.lpsThatNeedWhitelist.length > 0) {
                return (
                    <LpsThatNeedWhitelistNotification
                        lpsThatNeedWhitelist={calc.lpsThatNeedWhitelist}
                        language={language}
                        permitTypeId={storeState.config.permitTypeId}
                        update={update}
                    />
                );
            } else if (calc.badgesThatNeedWhitelist.length > 0) {
                return (
                    <BadgesThatNeedWhitelistNotification
                        badgesThatNeedWhitelist={calc.badgesThatNeedWhitelist}
                        language={language}
                        permitTypeId={storeState.config.permitTypeId}
                        update={update}
                    />
                );
            } else if (calc.overlappingLpPermits.length > 0) {
                return (
                    <OverlappingEntitiesNotification
                        overlaps={calc.overlappingLpPermits}
                        language={language}
                        identificationType="LicensePlate"
                    />
                );
            } else if (calc.overlappingBadgePermits.length > 0) {
                return (
                    <OverlappingEntitiesNotification
                        overlaps={calc.overlappingBadgePermits}
                        language={language}
                        identificationType="Badge"
                    />
                );
            } else if (calc.badgesThatNeedAccount.length > 0) {
                return (
                    <BadgesThatNeedAccountNotification
                        badgesThatNeedAccount={calc.badgesThatNeedAccount}
                        language={language}
                    />
                );
            } else if (calc.quotaInfo) {
                return (
                    <>
                        {calc.quotaInfo.map(infoItem => (
                            <QuotaReachedInfo
                                key={keyForQuotaInfo(infoItem)}
                                quotaInfo={infoItem}
                                quotasWrite={quotasWrite}
                            />
                        ))}
                    </>
                );
            }
        }
        return null;
    }

    function checkQuotaApprovalRights(): boolean {
        const qutoaInfo = storeState.calc.data?.quotaInfo;
        const quotaOverlapping =
            qutoaInfo?.some(
                quotaInfo =>
                    quotaInfo.type ==
                    ContractQuotaInfoType.CONTRACT_PURCHASE_OVERLAPPING,
            ) || false;
        const quotaWrites =
            storeState.currentLogin.data?.permissions.quotasWrite;
        return !quotaOverlapping || Boolean(quotaWrites);
    }

    function renderHeader(): JSX.Element {
        if (storeState.createResponse.pending || storeState.calc.pending) {
            return <LoaderHeader title={txt().HeaderCaption()} />;
        }

        if (
            storeState.createResponse.errorData ||
            (incompleteData() && storeState.layout.confirmPressedOnError)
        ) {
            return (
                <ErrorHeader
                    language={language}
                    title={
                        storeState.createResponse.errorData?.code === -140 ? (
                            <Localized
                                de="Vom Datum ausserhalb des erlaubten Bereichs"
                                fr="Date de début hors de la période autorisée"
                                it="Data d’inizio fuori intervallo consentito"
                                en="From date outside allowed range"
                            />
                        ) : (
                            <Localized
                                de="Fehler beim Erstellen der Bewilligung"
                                fr="Erreur lors de la création du permis"
                                it="Errore durante la creazione del permesso"
                                en="Error on creating the permit"
                            />
                        )
                    }
                    onCancel={() => update(abortPermitCreateProcess)}
                />
            );
        }

        return (
            <ConfirmSaveHeader
                language={language}
                title={txt().HeaderCaption()}
                onCancel={() => update(abortPermitCreateProcess)}
                onSave={() => {
                    if (!checkQuotaApprovalRights()) {
                        // Do nothing
                    } else if (incompleteData()) {
                        update(store =>
                            PermitCreateState.Layout.stateWrite(store, {
                                confirmPressedOnError: true,
                            }),
                        );
                    } else {
                        update(store =>
                            createPermit(
                                store,
                                PermitCreateState.makePermitPayload(store) ??
                                    undefined,
                            ),
                        );
                    }
                }}
                confirmButtonSpecialState={confirmButtonSpecialState()}
            />
        );
    }

    const create = storeState.config;
    const updater = update;
    const startDate =
        storeState.calc.data?.validFrom ?? storeState.config.startDate;
    const endDate = storeState.calc.data?.validTo ?? storeState.config.endDate;
    const type = storeState.permitType;
    const createTexts = PermitCreateTexts.permitTypeSelection[language];

    return (
        <div>
            <Dialog open={storeState.createResponse.errorData?.code === -710}>
                <DialogTitle
                    sx={theme => ({
                        background: theme.palette['error'].main,
                        color: theme.palette['error'].contrastText,
                        paddingY: 1,
                    })}
                >
                    <Localized
                        de={'Badge-Fehler'}
                        fr={'Erreur badge'}
                        it={'Errore badge'}
                        en={'Badge error'}
                    />
                </DialogTitle>
                <DialogContent>
                    <p>
                        <Localized
                            de={
                                'Es besteht ein Problem mit einem oder meheren badges.'
                            }
                            fr={
                                'Il y a un problème avec un ou plusieurs badges.'
                            }
                            it={
                                'Si è verificato un problema con uno o più badge.'
                            }
                            en={'There is a problem with one or more badges.'}
                        />
                        <br />
                        {storeState.createResponse.errorData?.message}
                    </p>
                </DialogContent>
                <DialogActions>
                    <Button
                        onClick={() =>
                            updater(PermitCreateState.CreateResponse.reset)
                        }
                    >
                        <Localized de="OK" fr="OK" it="OK" en="OK" />
                    </Button>
                </DialogActions>
            </Dialog>

            <SlideInBody disabled={false}>
                <FullSlideInLeftColumn>
                    <SingleSelection
                        selection={storeState.permitType?.description ?? ''}
                        focused={storeState.layout.permitTypeSelectionOpen}
                        label={createTexts.HeaderHeading()}
                        onClick={() => {
                            update(store =>
                                PermitCreateState.Layout.stateWrite(store, {
                                    permitTypeSelectionOpen: true,
                                }),
                            );
                        }}
                        errorText={permitTypeErrorText()}
                    />
                    {type ? (
                        <div>
                            <SingleSelection
                                focused={
                                    storeState.layout.validityFromDatePickerOpen
                                }
                                label={txt().StartDate()}
                                onClick={() =>
                                    update(store =>
                                        PermitCreateState.Layout.stateWrite(
                                            store,
                                            {
                                                validityFromDatePickerOpen: true,
                                            },
                                        ),
                                    )
                                }
                                errorText={validityIntervalErrorText()}
                                selection={
                                    startDate
                                        ? Formatter.dayMonthYear(startDate)
                                        : ' — '
                                }
                            />
                            {storeState.config.startDate &&
                                startDate &&
                                Formatter.isoYearMonthDay(
                                    storeState.config.startDate,
                                ) !== Formatter.isoYearMonthDay(startDate) && (
                                    <ChangedValidFromDateWarning />
                                )}
                            <DurationField
                                label={txt().Duration()}
                                onClick={() =>
                                    update(store =>
                                        PermitCreateState.Layout.stateWrite(
                                            store,
                                            {
                                                validityDurationPickerOpen: true,
                                            },
                                        ),
                                    )
                                }
                                errorText={validityIntervalErrorText()}
                                value={getSelectedDurationString()}
                                durationsAvailable={selectableDurationsCount()}
                            />
                            <EndDateField
                                label={txt().EndDate()}
                                selectedValue={
                                    endDate
                                        ? endDate >
                                          DateTime.fromISO('2030-01-01')
                                            ? txt().UntilRecall()
                                            : Formatter.dayMonthYear(endDate)
                                        : null
                                }
                                calculatedValue={getValidToCalcString()}
                                onClick={() =>
                                    update(store =>
                                        PermitCreateState.Layout.stateWrite(
                                            store,
                                            {
                                                validityToDatePickerOpen: true,
                                            },
                                        ),
                                    )
                                }
                                errorText={validityIntervalErrorText()}
                                endDateSelectable={isEndDateSelectable()}
                            />
                            <LicensePlates
                                canBeChanged={true}
                                lps={storeState.config.licensePlates}
                                maxIdentificationCount={type.maxLicenses}
                                onIdentificationClick={(id: number) =>
                                    update(store =>
                                        LicensePlateState.Entity.edit(
                                            store,
                                            id,
                                        ),
                                    )
                                }
                                onIdentificationAction={(id: number) =>
                                    update(store =>
                                        removeLicensePlate(store, id),
                                    )
                                }
                                onAdd={() =>
                                    update(store =>
                                        LicensePlateState.AddTripple.stateWrite(
                                            store,
                                            { createLpOpen: true },
                                        ),
                                    )
                                }
                                showError={
                                    storeState.layout.confirmPressedOnError
                                }
                            />
                            <Badges
                                canBeChanged={true}
                                badges={storeState.config.badges}
                                maxIdentificationCount={type.maxLicenses}
                                onIdentificationClick={() => null}
                                onIdentificationAction={(id: number) =>
                                    update(store => removeBadge(store, id))
                                }
                                onAdd={() =>
                                    update(store =>
                                        BadgeState.Add.stateWrite(store, {
                                            createBadgeOpen: true,
                                        }),
                                    )
                                }
                                showError={
                                    storeState.layout.confirmPressedOnError
                                }
                            />
                        </div>
                    ) : null}
                    {renderNotification()}
                    {type ? renderZones(type) : null}
                </FullSlideInLeftColumn>

                <FullSlideInRightColumn>
                    <LabeledText label={txt().Price()}>
                        {getPriceString()}
                    </LabeledText>

                    {storeState.operatorData.data
                        ?.requireCounterPaymentChannel && (
                        <CounterPaymentChannelEdit />
                    )}

                    <AddressSelection
                        address={storeState.config.address}
                        onEdit={() => update(editAddress)}
                        onClear={
                            storeState.config.address
                                ? () => update(clearAddress)
                                : null
                        }
                        disabled={false}
                        showRequiredError={
                            storeState.layout.confirmPressedOnError &&
                            PERMITTYPES_WITH_REQUIRED_ADDRESS.indexOf(
                                storeState.permitType?.id ?? -1,
                            ) > -1
                        }
                    />

                    {(create.additionalInfo && create.additionalInfo !== '') ||
                    (type && type.additionalInfoMode === 'edit') ? (
                        <AdditionalInfos
                            value={create.additionalInfo || ''}
                            onChange={(additionalInfo: string) =>
                                updater(store =>
                                    PermitCreateState.Config.stateWrite(store, {
                                        additionalInfo,
                                    }),
                                )
                            }
                            disabled={
                                !!type && type.additionalInfoMode === 'show'
                            }
                        />
                    ) : null}
                    <Remark
                        value={create.remark || ''}
                        disabled={false}
                        onChange={(v: string) =>
                            updater(store =>
                                PermitCreateState.Config.stateWrite(store, {
                                    remark: v,
                                }),
                            )
                        }
                    />
                    {type && type.mubstBeLinkedToCustomerAccount && (
                        <Email
                            value={storeState.config.email}
                            onChange={(v: string) => {
                                update(s => {
                                    const name =
                                        PermitCreateState.Config.stateWrite(s, {
                                            email: v,
                                        });
                                    PermitCreateState.Layout.stateWrite(s, {
                                        confirmPressedOnError: false,
                                    });
                                    return name;
                                });
                                resetCalc();
                            }}
                            error={lprEmailError(
                                storeState.layout.confirmPressedOnError,
                                storeState.calc.data || null,
                                storeState.config.email || '',
                            )}
                        />
                    )}

                    {hasLicensePlateIdentification(
                        getIdentificationTypes(storeState),
                    ) &&
                        type &&
                        type.allowedLicensePlateTypes.filter(
                            t => t.needsVignette,
                        ).length > 0 && (
                            <SingleSelection
                                focused={false}
                                label={txt().VignetteToInvoice()}
                                onClick={() =>
                                    update(store =>
                                        PermitCreateState.Layout.stateWrite(
                                            store,
                                            {
                                                vignetteCountSelectionOpen: true,
                                            },
                                        ),
                                    )
                                }
                                selection={
                                    storeState.config.vigetteAmount?.toString() ??
                                    null
                                }
                            />
                        )}
                </FullSlideInRightColumn>
            </SlideInBody>
            {renderHeader()}
        </div>
    );
}

function lprEmailError(
    confirmPressedOnError: boolean,
    calcData: PermitCreateState.Calc.CalcData | null,
    inputValue: string,
): Message | null {
    if (!confirmPressedOnError) {
        return null;
    }

    if (!inputValue) {
        return {
            de: 'Pflichtfeld',
            fr: 'Champ obligatoire',
            it: 'Campo obbligatorio',
            en: 'Required field',
        };
    }

    if (calcData && calcData.emailIsNotALogin) {
        return {
            de: 'Diese E-Mail ist kein Parkingpay-Login oder hat die Registrierung nicht abgeschlossen',
            fr: "Cet e-mail n'est pas un login Parkingpay ou n'a pas terminé l'enregistrement",
            it: 'Questa e-mail non è un login Parkingpay o non ha completato la registrazione',
            en: 'This e-mail is not a Parkingpay login or has not completed the registration',
        };
    }

    return null;
}

function toLuxonDuration(durationType: DurationType, value: number): Duration {
    switch (durationType) {
        case 'hour':
            return Duration.fromObject({ hours: value });
        case 'day':
            return Duration.fromObject({ days: value });
        case 'week':
            return Duration.fromObject({ weeks: value });
        case 'month':
            return Duration.fromObject({ months: value });
        case 'year':
            return Duration.fromObject({ years: value });
        case 'date':
            console.error(
                `'date' is not a valid moment time unit. 'days' are used instead.`,
            );
            return Duration.fromObject({ days: value });
    }
}

function QuotaReachedInfo({
    quotaInfo,
    quotasWrite,
}: {
    quotaInfo: ContractQuotaInfos;
    quotasWrite: boolean;
}) {
    switch (quotaInfo.type) {
        case ContractQuotaInfoType.CONTRACT_PURCHASE_OVERLAPPING:
            return (
                <Notification
                    color={
                        quotasWrite
                            ? NotificationColor.blue
                            : NotificationColor.error
                    }
                    icon={NotificationIcon.info}
                    title={<ContractQuotaInfoPerPeriodTitle />}
                >
                    <ContractQuotaInfoPerPeriodBody />
                </Notification>
            );
        case ContractQuotaInfoType.CONTRACT_PURCHASE_PER_ENTITY: {
            const fullyReached = quotaInfo.quotaReachingEntities.every(
                e => e.daysLeft === 0,
            );
            return (
                <Notification
                    color={NotificationColor.blue}
                    icon={NotificationIcon.info}
                    title={
                        <ContractQuotaInfoPerEntityTitle
                            fullyReached={fullyReached}
                        />
                    }
                >
                    <ContractQuotaInfoPerEntityBody
                        quotaInfo={quotaInfo}
                        fullyReached={fullyReached}
                    />
                </Notification>
            );
        }
        default:
            console.error(
                `Unknown ContractQuotaInfo '${quotaInfo}'. Contract purchase is blocked without a user message.`,
            );
            return null;
    }
}

function ChangedValidFromDateWarning() {
    return (
        <Notification
            title={
                <Localized
                    de="Datum angepasst"
                    fr="Date adaptée"
                    it="Data modificata"
                    en="Date adjusted"
                />
            }
            color={NotificationColor.blue}
            icon={NotificationIcon.info}
        >
            <Localized
                de="Das ausgewählte Datum ist nicht verfügbar, daher wurde auf das nächste verfügbare Datum umgestellt."
                fr="La date sélectionnée n'est pas disponible, elle a donc été modifiée à la prochaine date disponible."
                it="La data selezionata non è disponibile, quindi è stata cambiata sulla prossima data disponibile."
                en="The selected date is not available, therefore it has been changed to the next available date."
            />
        </Notification>
    );
}

function ContractQuotaInfoPerEntityTitle({
    fullyReached,
}: {
    fullyReached: boolean;
}) {
    return fullyReached ? (
        <Localized
            de="Kontingent erschöpft"
            fr="Contingent épuisé"
            it="Contingente esaurito"
            en="Quota exhausted"
        />
    ) : (
        <Localized
            de="Ungenügendes Kontingent"
            fr="Contingent insuffisant"
            it="Contingente insufficiente"
            en="Insufficient quota"
        />
    );
}

function ContractQuotaInfoPerEntityBody({
    quotaInfo,
    fullyReached,
}: {
    quotaInfo: ContractQuotaInfosPerEntity;
    fullyReached: boolean;
}) {
    return (
        <>
            <p>
                <Localized
                    de={`Diese Bewilligungsart unterliegt einer Kontingentierung und kann daher vom Benutzer nur für ${
                        quotaInfo.days
                    } Tage ${
                        quotaInfo.period === 'CALENDAR_MONTH'
                            ? 'pro Monat'
                            : 'pro Jahr'
                    } erworben werden.`}
                    fr={`Ce type d'autorisation est soumis à un contingent et ne peut donc être acheté par l'utilisateur que pour ${
                        quotaInfo.days
                    } jours ${
                        quotaInfo.period === 'CALENDAR_MONTH'
                            ? 'par mois'
                            : 'par année'
                    }.`}
                    it={`Questo tipo di autorizzazione è soggetta a un contingente e quindi può essere acquistata dall'utente solo per ${
                        quotaInfo.days
                    } giorni ${
                        quotaInfo.period === 'CALENDAR_MONTH'
                            ? 'al mese'
                            : "all'anno"
                    }.`}
                    en={`This type of permit is subject to a quota and can therefore only be purchased by the user for ${
                        quotaInfo.days
                    } days ${
                        quotaInfo.period === 'CALENDAR_MONTH'
                            ? 'per month'
                            : 'per year'
                    }.`}
                />
            </p>
            {!fullyReached && (
                <>
                    <p>
                        <Localized
                            de={`Das Kontingent für die folgenden ${
                                quotaInfo.entityType === 'LICENSE_PLATE'
                                    ? 'Kennzeichen'
                                    : 'Badge'
                            } ist unzureichend:`}
                            fr={`Le contingent pour ${
                                quotaInfo.entityType === 'LICENSE_PLATE'
                                    ? 'les plaques suivantes'
                                    : 'les badges suivants'
                            } est insuffisant.`}
                            it={`Il contingente per ${
                                quotaInfo.entityType === 'LICENSE_PLATE'
                                    ? 'le seguenti targhe'
                                    : 'i seguenti badges'
                            } è insufficiente:`}
                            en={`The quota for the following ${
                                quotaInfo.entityType === 'LICENSE_PLATE'
                                    ? 'license plates'
                                    : 'badges'
                            } is insufficient:`}
                        />
                    </p>
                    <p>
                        {quotaInfo.quotaReachingEntities
                            .filter(e => e.notEnoughQuota)
                            .map(e => (
                                <div key={e.description}>
                                    <strong>{e.description}</strong>
                                    {' – '}
                                    {e.daysLeft > 0 ? (
                                        <Localized
                                            de={`${e.daysLeft} ${
                                                e.daysLeft > 1 ? 'Tage' : 'Tag'
                                            } verfügbar`}
                                            fr={`${e.daysLeft} ${
                                                e.daysLeft > 1
                                                    ? 'jours disponibles'
                                                    : 'jour disponible'
                                            }`}
                                            it={`${e.daysLeft} ${
                                                e.daysLeft > 1
                                                    ? 'giorni disponibili'
                                                    : 'giorno disponibile'
                                            }`}
                                            en={`${e.daysLeft} ${
                                                e.daysLeft > 1 ? 'days' : 'day'
                                            } available`}
                                        />
                                    ) : (
                                        <Localized
                                            de="verfügbare Tage verbraucht"
                                            fr="jours disponibles épuisés"
                                            it="giorni a disposizione esauriti"
                                            en="days available sold out"
                                        />
                                    )}
                                </div>
                            ))}
                    </p>
                </>
            )}
        </>
    );
}

function ContractQuotaInfoPerPeriodTitle() {
    return (
        <Localized
            de="Kontingent erschöpft"
            fr="Contingent épuisé"
            it="Contingente esaurito"
            en="Quota exhausted"
        />
    );
}

function ContractQuotaInfoPerPeriodBody() {
    return (
        <p>
            <Localized
                de="Das Kontingent für diese Bewilligung ist für mindestends einen Tag im ausgewählten Zeitraum erschöpft."
                fr="Le contingent pour cette autorisation est épuisé pour au moins un jour dans la période sélectionnée."
                it="Il contingente per questa autorizzazione è esaurito per almeno un giorno nel periodo selezionato."
                en="The quota for this permit is exhausted for at least one day in the selected period."
            />
        </p>
    );
}
